import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect, useState } from "react";
import LuxuryScrollProvider from "./components/LuxuryScrollProvider";
import PageLoader from "./components/PageLoader";

// Eager load Index for fast initial render
import Index from "./pages/Index";

const AnalyticsProvider = lazy(() => import("./components/AnalyticsProvider"));

// Lazy load other pages for better performance
const NotFound = lazy(() => import("./pages/NotFound"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CaseStudiesPage = lazy(() => import("./pages/CaseStudiesPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));

// Lazy load Service Pages
const ShopifyDesign = lazy(() => import("./pages/services/ShopifyDesign"));
const ShopifyManagement = lazy(() => import("./pages/services/ShopifyManagement"));
const PerformanceMarketing = lazy(() => import("./pages/services/PerformanceMarketing"));
const WebDesign = lazy(() => import("./pages/services/WebDesign"));
const AIAutomation = lazy(() => import("./pages/services/AIAutomation"));
const POSSoftware = lazy(() => import("./pages/services/POSSoftware"));

// Minimal loading fallback
const PageFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const DeferredAnalytics = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 12000);
    return () => window.clearTimeout(id);
  }, []);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <AnalyticsProvider />
    </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
    <LuxuryScrollProvider>
          <PageLoader>
            <BrowserRouter>
              <DeferredAnalytics />
                <Suspense fallback={<PageFallback />}>
                  <main>
                    <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/case-studies" element={<CaseStudiesPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/blog" element={<BlogPage />} />

                    {/* Service Detail Pages */}
                    <Route path="/services/shopify-design" element={<ShopifyDesign />} />
                    <Route path="/services/shopify-management" element={<ShopifyManagement />} />
                    <Route path="/services/performance-marketing" element={<PerformanceMarketing />} />
                    <Route path="/services/web-design" element={<WebDesign />} />
                    <Route path="/services/ai-automation" element={<AIAutomation />} />
                    <Route path="/services/pos-software" element={<POSSoftware />} />
                    <Route path="/services/pos" element={<Navigate to="/services/pos-software" replace />} />
                    <Route path="/services/possoftware" element={<Navigate to="/services/pos-software" replace />} />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                </Suspense>
            </BrowserRouter>
          </PageLoader>
    </LuxuryScrollProvider>
  </HelmetProvider>
);

export default App;
